import styled from 'styled-components';
import React, { useMemo } from 'react';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { FaChevronLeft, FaRegStar, FaStar } from 'react-icons/fa';
import { useImageThumbnailGallery } from '@/features/shared/experience-info-modal/use-image-thumbnail-gallery';
import {
  ImageEditorContext,
  newImageEditorContext,
} from '@/features/editor/widgets/custom-widget/inputs/background/image/shared/context';
import { ImageEditor } from '@/features/editor/widgets/custom-widget/inputs/background/image/editor';
import Portal from '@/components/portal';
import { CloseButton } from '@/components/close-button';
import { PopConfirm } from '@/components/pop-confirm';
import { BackdropSpinner } from '@/components/backdrop-spinner';

export interface ImageThumbnailGalleryProps {
  initialValue?: string[];
  maxImages?: number;
  allowUpload?: boolean;
  onImageListChanged?: (list: string[], id?: string) => void;
  isExpDetailsEnhancement?: boolean;
  isFavoriteStarUpdate?: boolean;
  favoriteImage?: string | null | undefined;
}

export function ImageThumbnailGallery({
  onImageListChanged,
  initialValue,
  maxImages,
  allowUpload,
  isExpDetailsEnhancement,
  isFavoriteStarUpdate,
  favoriteImage,
}: ImageThumbnailGalleryProps) {
  const ctx = newImageEditorContext(``);
  const { image: uploadedImage, setImage: setUploadedImage } = ctx;

  const {
    loading,
    getRootProps,
    getInputProps,
    style,
    onCancelUpload,
    onUploadBlob,
    imageList,
    imageCount,
    maxImagesToShow,
    isSliderOpen,
    setIsSliderOpen,
    sliderImage,
    onThumbnailClicked,
    onImageClicked,
    onImageDelete,
    onFavoriteClicked,
  } = useImageThumbnailGallery(
    maxImages,
    uploadedImage,
    setUploadedImage,
    onImageListChanged,
    initialValue,
  );

  const isFavoriteExists = useMemo(
    () => (favoriteImage ? imageList?.includes(favoriteImage) : false),
    [favoriteImage, imageList],
  );
  const orderedImages = useMemo(() => {
    if (isFavoriteExists) {
      return [
        favoriteImage,
        ...imageList.filter((img) => img !== favoriteImage),
      ];
    }
    return imageList;
  }, [imageList, favoriteImage]);

  return (
    <ImageEditorContext.Provider value={ctx}>
      <Wrapper>
        {!!uploadedImage && (
          <Portal selector="#shared-elem">
            <OverlayWrapper>
              <BackdropSpinner loading={loading} />
              <DiscardUploadButton onClick={onCancelUpload}>
                Cancel
              </DiscardUploadButton>
              <ImageEditor buttonText="Upload" onUpdatePreview={onUploadBlob} />
            </OverlayWrapper>
          </Portal>
        )}
        {isSliderOpen &&
          (isExpDetailsEnhancement ? (
            <Portal selector="body">
              <ImageZoomWrapper onClick={() => setIsSliderOpen(false)}>
                <BackButtonWrapper>
                  <FaChevronLeft color="#979a9e" size={25} />
                </BackButtonWrapper>
                <div className="img-container">
                  <img src={sliderImage} alt={sliderImage} />
                </div>
              </ImageZoomWrapper>
            </Portal>
          ) : (
            <Portal selector="#shared-elem">
              <OverlayWrapper>
                <SliderWrapper>
                  <CloseWrapper>
                    <CloseButton onClick={() => setIsSliderOpen(false)} />
                  </CloseWrapper>
                  <BigImage src={sliderImage} alt="" />
                  <ThumbnailsWrapper>
                    {imageList?.map((img) => (
                      <Image
                        key={img}
                        onClick={() => onThumbnailClicked(img)}
                        src={img}
                      />
                    ))}
                  </ThumbnailsWrapper>
                </SliderWrapper>
              </OverlayWrapper>
            </Portal>
          ))}
        {allowUpload && imageCount < maxImagesToShow && (
          <StyledDropzone {...getRootProps({ style })}>
            <input {...getInputProps()} />
          </StyledDropzone>
        )}
        {orderedImages?.map((img, index) => (
          <ThumbnailWrapper key={img}>
            <div
              className={`delete-image ${
                isFavoriteStarUpdate ? `in-circle` : ``
              }`}
            >
              <PopConfirm
                payload={img}
                onConfirm={onImageDelete}
                text="Are you sure you want to delete this image?"
              >
                <RiDeleteBin5Line />
              </PopConfirm>
            </div>
            {isFavoriteStarUpdate && imageList.length > 1 && (
              <div
                className={`star-image ${
                  !isFavoriteExists && index === 0 ? `default-favorite` : ``
                } ${
                  img === favoriteImage || (!isFavoriteExists && index === 0)
                    ? `show-favorite`
                    : ``
                }`}
              >
                {img === favoriteImage || (!isFavoriteExists && index === 0) ? (
                  <FaStar
                    onClick={() => {
                      onFavoriteClicked(null);
                    }}
                  />
                ) : (
                  <FaRegStar
                    onClick={() => {
                      onFavoriteClicked(img);
                    }}
                  />
                )}
              </div>
            )}
            <Image onClick={() => onImageClicked(img)} src={img} />
          </ThumbnailWrapper>
        ))}
      </Wrapper>
    </ImageEditorContext.Provider>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  width: 100%;
  height: 100%;
  padding: 1rem 0;

  && > * {
    height: 6rem;
    width: 6rem;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  &&:hover {
    .delete-image,
    .star-image {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .delete-image,
  .star-image {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out, pointer-events 0.2s ease-in-out;
    transition-delay: 0.5s;
    cursor: context-menu;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0.1rem;
    right: 0.1rem;
    font-size: 1.8rem;
    color: black;
    padding: 0.2rem;
    background: rgb(0, 0, 0, 0.1);
    border-radius: 0.2rem 0.9rem 0.2rem 0.2rem;
  }

  .star-image,
  .in-circle {
    top: -0.8rem;
    left: -0.8rem;
    font-size: 1.6rem;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0;
    border-radius: 100%;
    background: rgb(255, 255, 255);
    box-shadow: rgba(0, 0, 0, 0.44) 0 3px 8px;
  }

  .delete-image.in-circle {
    right: -1rem;
    left: unset;
    padding-top: 5px;
  }

  .default-favorite {
    pointer-events: none !important;
    cursor: default !important;
  }
  .show-favorite {
    opacity: 1 !important;
  }
`;

const Image = styled.img`
  cursor: zoom-in;
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: 1rem;
  border: 1px solid #979797;
  background: white;
`;

const OverlayWrapper = styled.div`
  z-index: 100000000000;
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
`;

const SliderWrapper = styled.div`
  background: white;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const ThumbnailsWrapper = styled.div`
  position: absolute;
  bottom: 2rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  box-shadow: 0 0 1rem rgba(0, 0, 0, 0.1);
  background: rgb(0, 0, 0, 0.05);
  border-radius: 2rem;

  && > * {
    height: 6rem;
    width: 6rem;
  }
`;

const BigImage = styled.img`
  height: 90%;
  width: auto;
  object-fit: contain;
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: 2.5rem;
  right: 2.5rem;
  z-index: 100;
  opacity: 0.37;

  && > * {
    height: 3rem;
    width: 3rem;
  }
`;

const DiscardUploadButton = styled.button`
  position: absolute;
  appearance: none;
  outline: none;
  border: 1px solid gray;
  top: 1.18rem;
  right: 9rem;
  z-index: 10;
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  font-size: 1.3rem;
  cursor: pointer;

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 0.6;
  }
`;

const StyledDropzone = styled.div`
  background: #f3f3f3;
  border-radius: 1rem;
  position: relative;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 0.6;
  }

  &&:after {
    top: -2px;
    position: absolute;
    height: 100%;
    width: 100%;
    content: '+';
    font-size: 2.5rem;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #b4bbbe;
  }
`;

const ImageZoomWrapper = styled.div`
  position: fixed;
  inset: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  z-index: 1000000000000000000;

  background: rgba(238, 238, 238, 0.76);
  backdrop-filter: blur(40px);

  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;

  && * {
    user-select: none;
  }

  .img-container {
    padding: 0;
    margin: 0;
    position: relative;
    height: auto;
    width: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 90vh;
    max-width: 90vw;
  }

  img {
    display: flex;
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-height: 90vh;
    max-width: 90vw;
    border-radius: 40px;
    box-shadow: rgba(0, 0, 0, 0.35) 0 10px 15px;
  }
`;

const BackButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  left: 3rem;
  background: white;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  box-shadow: rgba(100, 100, 111, 0.35) 0 7px 29px 0;
  cursor: pointer;
  width: 50px;
  height: 50px;
`;
